import { ApplicationPathType } from '@modules/test-development/enums/application-path-type.enum';
import { Platform } from '@modules/test-development/enums/platform.enum';
import { alias, custom, deserialize, serializable } from 'serializr';
import { Base } from '../../../models/base.model';
import { Deserializable } from '../../../models/deserializable';
import { Capability } from './capability.model';

export class EnvironmentSettings extends Base implements Deserializable {
  @serializable
  public title: string;
  @serializable
  public gridUrl: string;
  @serializable(
    custom(
      (v) => {
        if (v == Platform.Windows || v == 'WINDOWS') return '2';
        if (v == Platform.Mac || v == 'MACOSX') return '3';
        if (v == Platform.Linux || v == 'LINUX') return '4';
        if (v == Platform.Android) return '5';
        if (v == Platform.iOS) return '6';
        return v;
      },
      (v) => {
        if (v == '2') return Platform.Windows;
        if (v == '3') return Platform.Mac;
        if (v == '4') return Platform.Linux;
        if (v == '5') return Platform.Android;
        if (v == '6') return Platform.iOS;
        return v;
      },
    ),
  )
  public platform: Platform;
  @serializable
  public osVersion: string;
  @serializable(
    custom(
      (v) => {
        if (v && v.toUpperCase() == 'MOZILLAFIREFOX') return 'FIREFOX';
        else if (v && v.toUpperCase() == 'MICROSOFTEDGE') return 'EDGE';
        return v;
      },
      (v) => {
        if (v && v.toUpperCase() == 'FIREFOX') return 'MOZILLAFIREFOX';
        else if (v && v.toUpperCase() == 'EDGE') return 'MICROSOFTEDGE';
        return v;
      },
    ),
  )
  public browser: string;
  @serializable
  public browserVersion: string;
  @serializable
  public resolution: string;
  @serializable
  public deviceName: string;
  @serializable
  public appiumUrl: string;
  @serializable
  public udid: string;
  @serializable(
    alias(
      'apksrcType',
      custom(
        (v) => {
          if (v == ApplicationPathType.USE_PATH) return '1';
          if (v == ApplicationPathType.APP_DETAILS) return '2';
          if (v == ApplicationPathType.UPLOADS) return '3';
        },
        (v) => {
          if (v == '1' || v == 'USE_PATH') return ApplicationPathType.USE_PATH;
          if (v == '2' || v == 'APP_DETAILS')
            return ApplicationPathType.APP_DETAILS;
          if (v == '3' || v == 'UPLOADS') return ApplicationPathType.UPLOADS;
          return v;
        },
      ),
    ),
  )
  public apkSrcType: ApplicationPathType;
  @serializable(alias('app_upload_id'))
  public appUploadId: number;
  @serializable
  public appPackage: string;
  @serializable
  public appActivity: string;
  @serializable(
    custom(
      (v) => {
        if (v == ApplicationPathType.USE_PATH) return '1';
        if (v == ApplicationPathType.APP_DETAILS) return '2';
        if (v == ApplicationPathType.UPLOADS) return '3';
      },
      (v) => {
        if (v == '1') return ApplicationPathType.USE_PATH;
        if (v == '2') return ApplicationPathType.APP_DETAILS;
        if (v == '3') return ApplicationPathType.UPLOADS;
      },
    ),
  )
  public pathType: ApplicationPathType;
  @serializable
  public apkPath: string;
  @serializable
  public appUrl: string;
  @serializable(
    alias(
      'ipasrcType',
      custom(
        (v) => {
          if (v == ApplicationPathType.USE_PATH) return '1';
          if (v == ApplicationPathType.APP_DETAILS) return '2';
          if (v == ApplicationPathType.UPLOADS) return '3';
        },
        (v) => {
          if (v == '1') return ApplicationPathType.USE_PATH;
          if (v == '2') return ApplicationPathType.APP_DETAILS;
          if (v == '3') return ApplicationPathType.UPLOADS;
          return v;
        },
      ),
    ),
  )
  public iPASrcType: ApplicationPathType;
  @serializable
  public bundleId: number;
  @serializable(
    custom(
      (v) => {
        if (v == ApplicationPathType.USE_PATH) return '1';
        if (v == ApplicationPathType.APP_DETAILS) return '2';
        if (v == ApplicationPathType.UPLOADS) return '3';
      },
      (v) => {
        if (v == '1') return ApplicationPathType.USE_PATH;
        if (v == '2') return ApplicationPathType.APP_DETAILS;
        if (v == '3') return ApplicationPathType.UPLOADS;
        return v;
      },
    ),
  )
  public appPathType: ApplicationPathType;
  @serializable
  public app: string;
  @serializable(
    custom(
      (v) => {
        const arr = [];
        if (v) {
          if (!(v instanceof Array)) {
            v = JSON.parse(v);
          }
          v.forEach((capability) => {
            const key = Object.keys(capability);
            if (
              capability[key[0]]?.length &&
              capability[key[1]]?.length &&
              capability[key[2]]?.length
            ) {
              arr.push(capability.serialize());
            }
          });
        }
        return JSON.stringify(arr);
      },
      (v) => {
        const capabilities = [];
        if (typeof v == 'string') {
          if (/\\\\"/g.test(v)) {
            v = v.replace(/\\"/g, '"');
            v = v.replace(/\\"/g, '"');
          } else if (v.startsWith('[{\\"')) {
            v = v.replace(/\\"/g, '"');
          }
          v = JSON.parse(v);
          v.forEach((capability) =>
            capabilities.push(new Capability().deserialize(capability)),
          );
        } else if (v instanceof Object)
          v.forEach((capability) =>
            capabilities.push(new Capability().deserialize(capability)),
          );
        return capabilities;
      },
    ),
  )
  public capabilities: Capability[];
  @serializable
  public type: number;
  @serializable(alias('create_session_at_case_level'))
  public createSessionAtCaseLevel: boolean;
  @serializable
  public resetSessionAtSuiteLevel: boolean = false;
  @serializable
  public runTestSuitesInParallel: boolean;
  @serializable
  public runTestCasesInParallel: boolean;
  @serializable
  public externalAgentType: string;
  @serializable
  public externalAgentUrl: string;

  get isWindows() {
    return this.platform == Platform.Windows;
  }

  get isMac() {
    return this.platform == Platform.Mac;
  }

  get isIOS() {
    return this.platform == Platform.iOS;
  }

  get isLinux() {
    return this.platform == Platform.Linux;
  }

  get isAndroid() {
    return this.platform == Platform.Android;
  }

  get isChrome() {
    return (
      this.browser?.toUpperCase() == 'GOOGLECHROME' ||
      this.browser?.toUpperCase() == 'CHROME'
    );
  }

  get isFirefox() {
    return (
      this.browser?.toUpperCase() == 'FIREFOX' ||
      this.browser?.toUpperCase() == 'MOZILLAFIREFOX'
    );
  }

  get isSafari() {
    return this.browser?.toUpperCase() == 'SAFARI';
  }

  get isEdge() {
    return (
      this.browser?.toUpperCase() == 'EDGE' ||
      this.browser?.toUpperCase() == 'MICROSOFTEDGE'
    );
  }

  get isIE() {
    return this.browser?.toUpperCase() == 'INTERNETEXPLORER';
  }

  get isMobile(): boolean {
    return this.isAndroid || this.isIOS;
  }

  deserialize(input: any): this {
    return Object.assign(this, deserialize(EnvironmentSettings, input));
  }
}
